import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import useSubscriptionStatus from '../hooks/useSubscriptionStatus';
import { isSubscriptionExemptPath } from '../utils/vendorPlanAccess';

const EXPIRED_MODAL_DISMISSED_KEY = 'vendor-expired-modal-dismissed';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [modalVariant, setModalVariant] = useState('onboarding');
  const [expiredModalDismissed, setExpiredModalDismissed] = useState(
    () => sessionStorage.getItem(EXPIRED_MODAL_DISMISSED_KEY) === '1'
  );
  const { user } = useAuth();
  const { hasActiveSubscription, isChecking, checkSubscriptionStatus } = useSubscriptionStatus();

  const subscriptionExpired =
    user?.user_type === 'vendor' && hasActiveSubscription === false && !isChecking;

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      setShowSubscriptionModal(false);
      setModalVariant('onboarding');
    }
  }, [user?.user_type]);

  useEffect(() => {
    if (hasActiveSubscription === true) {
      setShowSubscriptionModal(false);
      setModalVariant('onboarding');
      sessionStorage.removeItem(EXPIRED_MODAL_DISMISSED_KEY);
      setExpiredModalDismissed(false);
    }
  }, [hasActiveSubscription]);

  useEffect(() => {
    if (!subscriptionExpired || expiredModalDismissed) return;

    setModalVariant('expired');
    setShowSubscriptionModal(true);
  }, [subscriptionExpired, expiredModalDismissed]);

  useEffect(() => {
    const onSubscriptionRequired = () => {
      if (user?.user_type !== 'vendor') return;
      setModalVariant('expired');
      setShowSubscriptionModal(true);
      if (!isSubscriptionExemptPath(window.location.pathname)) {
        window.location.href = '/vendor/subscription';
      }
    };

    window.addEventListener('vendorSubscriptionRequired', onSubscriptionRequired);
    return () => window.removeEventListener('vendorSubscriptionRequired', onSubscriptionRequired);
  }, [user?.user_type]);

  const dismissExpiredModal = () => {
    sessionStorage.setItem(EXPIRED_MODAL_DISMISSED_KEY, '1');
    setExpiredModalDismissed(true);
    setShowSubscriptionModal(false);
  };

  const hideSubscriptionModal = () => {
    if (subscriptionExpired) {
      dismissExpiredModal();
      return;
    }
    setShowSubscriptionModal(false);
  };

  const openSubscriptionModal = () => {
    setModalVariant(subscriptionExpired ? 'expired' : 'onboarding');
    setShowSubscriptionModal(true);
  };

  const value = {
    hasActiveSubscription,
    subscriptionExpired,
    modalVariant,
    showSubscriptionModal: openSubscriptionModal,
    hideSubscriptionModal,
    dismissExpiredModal,
    checkSubscriptionStatus,
    isChecking,
    showSubscriptionModalState:
      showSubscriptionModal &&
      !(modalVariant === 'expired' && expiredModalDismissed) &&
      (modalVariant === 'expired' ||
        !isSubscriptionExemptPath(window.location.pathname)),
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
